import {inject, injectable} from "inversify";
import {EventEmitter} from "@/modules/base/events";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import {LiveMatchHandlerInterface} from "@/modules/match/interfaces";
import LiveMatchEvaluator from "@/modules/match/services/LiveMatchEvaluator";
import {LiveIncidentType, TickType} from "@/modules/match/types";
import chance from "@/modules/base/services/chance";
import {CardIncident} from "@/modules/match/entity";
import {CardEnum, NeutralIncidentTypeEnum} from "@/modules/match/enums";

@injectable()
export default class CardLiveMatchHandler implements LiveMatchHandlerInterface {
  private readonly playerCountLimit: number;
  private readonly averageYellowCardPerMatch: number;
  private readonly averageRedPerMatch: number;

  private yellowCardPlayerIds: number[] = [];

  constructor(
    @inject(EventEmitter) private em: EventEmitter,
    @inject(ConfigRepository) private configRepository: ConfigRepository,
  ) {
    this.playerCountLimit = this.configRepository.get('match.evaluation.playerCountLimit');
    this.averageYellowCardPerMatch = this.configRepository.get('match.evaluation.averageYellowCardPerMatch');
    this.averageRedPerMatch = this.configRepository.get('match.evaluation.averageRedPerMatch');
  }

  priority(): number {
    return 0;
  }

  async onFinish(liveMatchEvaluator: LiveMatchEvaluator, tick: TickType) {
  }

  async onIncident(liveMatchEvaluator: LiveMatchEvaluator, tick: LiveIncidentType): Promise<void> {
    if (
      tick.incident instanceof CardIncident
      && [CardEnum.RED, CardEnum.YELLOW_RED].includes(tick.incident.card)
    ) {
      const playerId = tick.incident.playerId!;
      tick.evaluator.suspendedPlayerIds.push(playerId);
      tick.evaluator.homePlayers = tick.evaluator.homePlayers.filter(p => p.id !== playerId);
      tick.evaluator.awayPlayers = tick.evaluator.awayPlayers.filter(p => p.id !== playerId);

      const teamId = tick.incident.player!.teamId;

      if (
        liveMatchEvaluator
          .getIncidents()
          .filter(
            i => i instanceof CardIncident
              && i.player
              && i.player.teamId === teamId
              && [CardEnum.RED, CardEnum.YELLOW_RED].includes(i.card)
          )
          .length > 4
      ) {
        tick.match.homeGoals = tick.match.homeTeam.id === teamId ? 0 : 6;
        tick.match.awayGoals = tick.match.awayTeam.id === teamId ? 0 : 6;

        await this.end(liveMatchEvaluator, tick);
      }
    }
  }

  async onTick(liveMatchEvaluator: LiveMatchEvaluator, tick: TickType): Promise<void> {
    if (tick.minute === 0) {
      await this.handleStartOfGame(liveMatchEvaluator, tick);
      return;
    }

    if (chance.bool({likelihood: this.averageYellowCardPerMatch * 100 / 90})) {
      const team = chance.bool() ? tick.match.homeTeam : tick.match.awayTeam;
      const player = chance.pickone(
        team.players
          .filter(p => p.lineup)
          .filter(p => !tick.evaluator.suspendedPlayerIds.includes(p.id))
      );
      const card = this.yellowCardPlayerIds.includes(player.id) ? CardEnum.YELLOW_RED : CardEnum.YELLOW;
      const incident = await liveMatchEvaluator.createCardIncident(tick.match, team, player, card, tick.minute);

      await liveMatchEvaluator.triggerIncident(
        Object.assign({}, tick, {incident}),
      );

      if (card === CardEnum.YELLOW) {
        this.yellowCardPlayerIds.push(player.id);
      }
    }

    if (chance.bool({likelihood: this.averageRedPerMatch * 100 / 90})) {
      const team = chance.bool() ? tick.match.homeTeam : tick.match.awayTeam;
      const player = chance.pickone(
        team.players
          .filter(p => p.lineup)
          .filter(p => !tick.evaluator.suspendedPlayerIds.includes(p.id))
      );
      const incident = await liveMatchEvaluator.createCardIncident(tick.match, team, player, CardEnum.RED, tick.minute);

      await liveMatchEvaluator.triggerIncident(
        Object.assign({}, tick, {incident}),
      );
    }
  }

  private async handleStartOfGame(liveMatchEvaluator: LiveMatchEvaluator, tick: TickType) {
    if (
      liveMatchEvaluator.homePlayers.filter(p => p.lineup !== null).length < this.playerCountLimit
      || liveMatchEvaluator.awayPlayers.filter(p => p.lineup !== null).length < this.playerCountLimit
    ) {
      const linedupHomePlayers = liveMatchEvaluator.homePlayers.filter(p => p.lineup !== null);
      const linedupAwayPlayers = liveMatchEvaluator.awayPlayers.filter(p => p.lineup !== null);

      if (linedupHomePlayers.length < this.playerCountLimit && linedupAwayPlayers.length < this.playerCountLimit) {
        tick.match.homeGoals = 0;
        tick.match.awayGoals = 0;
      } else {
        tick.match.homeGoals = linedupHomePlayers.length < this.playerCountLimit ? 0 : 6;
        tick.match.awayGoals = linedupAwayPlayers.length < this.playerCountLimit ? 0 : 6;
      }

      await this.end(liveMatchEvaluator, tick);
    }
  }

  private async end(liveMatchEvaluator: LiveMatchEvaluator, tick: TickType) {
    tick.match.tooFewPlayers = true;

    await liveMatchEvaluator.triggerIncident(
      liveMatchEvaluator.createLiveIncident(
        await liveMatchEvaluator.createNeutralIncident(tick.match, NeutralIncidentTypeEnum.NOT_ENOUGH_PlAYERS)
      )
    );

    liveMatchEvaluator.end();
  }
}
